import {
  INSTANT_ESTIMATE_INCOME_STEP,
  INSTANT_ESTIMATE_MAX_INCOME,
  INSTANT_ESTIMATE_MIN_INCOME,
  INSTANT_ESTIMATE_RANGE_FACTOR,
  InstantEstimateConfig,
  InstantEstimateResult,
} from './instant-estimate.models';

export function roundToNearest100(value: number): number {
  return Math.round(value / 100) * 100;
}

export function clampIncome(income: number): number {
  if (!Number.isFinite(income)) {
    return INSTANT_ESTIMATE_MIN_INCOME;
  }
  return Math.min(
    INSTANT_ESTIMATE_MAX_INCOME,
    Math.max(INSTANT_ESTIMATE_MIN_INCOME, income)
  );
}

export function validateConfig(config: InstantEstimateConfig): string[] {
  const errors: string[] = [];

  if (!config.schoolId) {
    errors.push('Missing schoolId.');
  }
  if (!config.schoolName) {
    errors.push('Missing schoolName.');
  }
  if (!(config.costOfAttendance > 0)) {
    errors.push('Cost of attendance must be greater than zero.');
  }
  if (!config.estimates?.length) {
    errors.push('Estimates list is empty.');
    return errors;
  }

  const expectedCount =
    (INSTANT_ESTIMATE_MAX_INCOME - INSTANT_ESTIMATE_MIN_INCOME) /
      INSTANT_ESTIMATE_INCOME_STEP +
    1;
  if (config.estimates.length !== expectedCount) {
    errors.push(
      `Expected ${expectedCount} income bands, found ${config.estimates.length}.`
    );
  }

  for (let i = 0; i < config.estimates.length; i++) {
    const point = config.estimates[i];
    const expectedIncome =
      INSTANT_ESTIMATE_MIN_INCOME + i * INSTANT_ESTIMATE_INCOME_STEP;

    if (point.income !== expectedIncome) {
      errors.push(
        `Income band ${i} expected ${expectedIncome}, found ${point.income}.`
      );
    }
    if (point.netPrice < 0) {
      errors.push(`Negative net price at income ${point.income}.`);
    }
    if (point.netPrice > config.costOfAttendance) {
      errors.push(
        `Central estimate ${point.netPrice} exceeds cost of attendance ${config.costOfAttendance} at income ${point.income}.`
      );
    }
  }

  return errors;
}

/** Linear interpolation of central net price before caps/rounding. */
export function interpolateNetPrice(
  config: InstantEstimateConfig,
  income: number
): number {
  const clamped = clampIncome(income);
  const estimates = config.estimates;

  if (!estimates.length) {
    return 0;
  }

  if (clamped <= estimates[0].income) {
    return estimates[0].netPrice;
  }

  const last = estimates[estimates.length - 1];
  if (clamped >= last.income) {
    return last.netPrice;
  }

  const lowerIndex = Math.floor(
    (clamped - INSTANT_ESTIMATE_MIN_INCOME) / INSTANT_ESTIMATE_INCOME_STEP
  );
  const lower = estimates[lowerIndex];
  const upper = estimates[lowerIndex + 1];

  if (!upper || lower.income === upper.income) {
    return lower.netPrice;
  }

  const t = (clamped - lower.income) / (upper.income - lower.income);
  return lower.netPrice + t * (upper.netPrice - lower.netPrice);
}

export function estimateAtIncome(
  config: InstantEstimateConfig,
  income: number
): InstantEstimateResult {
  const validationErrors = validateConfig(config);
  const isValid = validationErrors.length === 0;
  const clampedIncome = clampIncome(income);
  const coa = config.costOfAttendance;

  if (!isValid) {
    return {
      income: clampedIncome,
      central: 0,
      rangeLow: 0,
      rangeHigh: 0,
      costOfAttendance: coa,
      isValid: false,
      validationErrors,
    };
  }

  const interpolated = interpolateNetPrice(config, clampedIncome);
  const central = roundToNearest100(Math.min(coa, Math.max(0, interpolated)));

  let rangeLow = Math.max(
    0,
    roundToNearest100(central * (1 - INSTANT_ESTIMATE_RANGE_FACTOR))
  );
  let rangeHigh = Math.min(
    roundToNearest100(coa),
    roundToNearest100(central * (1 + INSTANT_ESTIMATE_RANGE_FACTOR))
  );

  // Keep ordering coherent after rounding/caps
  if (rangeLow > central) {
    rangeLow = central;
  }
  if (rangeHigh < central) {
    rangeHigh = central;
  }
  // Never report high above actual COA after rounding up past it
  if (rangeHigh > coa) {
    rangeHigh = roundToNearest100(coa);
    if (rangeHigh > coa) {
      rangeHigh = Math.floor(coa / 100) * 100;
    }
    if (rangeHigh < central) {
      rangeHigh = central;
    }
  }

  return {
    income: clampedIncome,
    central,
    rangeLow,
    rangeHigh,
    costOfAttendance: coa,
    isValid: true,
    validationErrors: [],
  };
}

/** Series for chart: central, low, high at each income band (rounded/capped). */
export function buildChartSeries(config: InstantEstimateConfig): {
  incomes: number[];
  central: number[];
  rangeLow: number[];
  rangeHigh: number[];
} {
  const incomes: number[] = [];
  const central: number[] = [];
  const rangeLow: number[] = [];
  const rangeHigh: number[] = [];

  for (const point of config.estimates) {
    const result = estimateAtIncome(config, point.income);
    incomes.push(point.income);
    central.push(result.central);
    rangeLow.push(result.rangeLow);
    rangeHigh.push(result.rangeHigh);
  }

  return { incomes, central, rangeLow, rangeHigh };
}
